import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import DropdownButton from "react-bootstrap/DropdownButton";
import Spinner from "components/Spinner";
import OpenQueryReminderModal from "components/OpenQueryReminderModal";
import { PhGridPagination, PhGridFilter, usePagination, PhGridSort } from "components/PhGrid";
import { remindersEnabled } from "../../flags";
import { cookieAttributes } from "util/resumeCookieSettings";

/** ---------- Origin helpers (unify iframe src + postMessage target) ---------- */
const normalizeOrigin = (v) => {
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const ENV = process.env.REACT_APP_ENV; // e.g., "local"
const QUERY_UI_DOMAIN = normalizeOrigin(process.env.REACT_APP_QUERY_UI_CLOUDFRONT_DOMAIN);

/** Local dev uses localhost; otherwise use env-provided domain.
 *  If the env var is missing in non-local, we fail fast with example.invalid.
 */
const targetOrigin =
  ENV === "local" ? "http://localhost:8081" : (QUERY_UI_DOMAIN || "https://example.invalid");

function QueryRow(props) {
  const handleShowResubmitModal = () => {
    props.setModalCategoryId(props["category-id"]);
    props.setModalResubmitData({
      definition: props.definition,
      categoryId: props["category-id"],
      reportId: props.reportId,
      description: props.description,
    });
    props.setShowResubmitModal(true);
  };

  const handleShowReminderModal = () => {
    props.setReminderJob(props.job);
    props.setShowReminderModal(true);
  };

  return (
    <tr key={props.id}>
      <th scope="row">{props.description || ""}</th>
      <td>{props.id}</td>
      <td>{props.report.split(".").join(", ")}</td>
      <td>{props.dateCreated.toLocaleDateString()}</td>
      <td>{props.status}</td>
      <td>
        {props.status === "PROCESSED" ? (
          <ButtonGroup>
            <DropdownButton as={ButtonGroup} title="Actions" id="bg-nested-dropdown">
              <Dropdown.Item eventKey="1" onClick={handleShowResubmitModal}>
                Resubmit Query
              </Dropdown.Item>
              {remindersEnabled && (
                <Dropdown.Item eventKey="2" onClick={handleShowReminderModal}>
                  Schedule Query Reminder
                </Dropdown.Item>
              )}
            </DropdownButton>
          </ButtonGroup>
        ) : null}
      </td>
    </tr>
  );
}

async function getToken() {
  await Auth.currentAuthenticatedUser(); // ensure session exists
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function OpenQueryQueriesContainer() {
  useEffect(() => {
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`QUERY - /openquery/commercial`);
      const resumeCookie = Object.assign(cookieAttributes(), { name: 'platform_resume' }, { value: cookieValue });
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();
  }, []);

  const {
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    setPaginationSize,
    filterString,
    setFilterString,
    sortColumn,
    setSortColumn,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
  } = usePagination();

  const [queries, setQueries] = useState(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [modalCategoryId, setModalCategoryId] = useState("");
  const [modalResubmitData, setModalResubmitData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [reminderJob, setReminderJob] = useState(null);

  const handleCloseResubmitModal = () => setShowResubmitModal(false);
  const handleCloseReminderModal = () => setShowReminderModal(false);

  // Auth readiness + in-flight guard
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    // Warn if env missing in non-local
    if (ENV !== "local" && !QUERY_UI_DOMAIN) {
      console.error(
        "REACT_APP_QUERY_UI_CLOUDFRONT_DOMAIN is not set. Iframe will not load correctly."
      );
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setQueries(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch queries only when authenticated
  useEffect(() => {
    if (!ready) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const jobs = await API.get("OpenQueryAPI", `/openquery/jobs`);
        if (cancelled) return;
        const shaped = (jobs?.requests || []).map((job) => ({
          ...job,
          dateCreated: new Date(job.dateCreated),
        }));
        setQueries(shaped);
      } catch (err) {
        console.error("error fetching openquery jobs", err);
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  // iframe messaging (token + resubmit payload + PROXY for /openquery/jobs)
  const handleIframeRequests = useCallback(
    (e) => {
      // Only trust messages from the iframe origin we expect
      if (!e || e.origin !== targetOrigin) {
        return;
      }

      const iframeWindow = document.querySelector("iframe")?.contentWindow;
      if (!iframeWindow) return;

      const sendToken = async () => {
        try {
          const token = await getToken();
          // send structured token message (for new iframe code)
          iframeWindow.postMessage({ type: "TOKEN", token }, targetOrigin);
        } catch (err) {
          console.error("error sending token to iframe", err);
          iframeWindow.postMessage(
            { type: "TOKEN_ERROR", message: err?.message || "Token error" },
            targetOrigin
          );
        }
      };

      const sendQueryPayload = () => {
        if (modalResubmitData) {
          // keep legacy behavior: send the raw resubmit data object
          iframeWindow.postMessage(modalResubmitData, targetOrigin);
        }
      };

      // --- Legacy string messages (existing behavior) ---
      if (typeof e.data === "string") {
        if (e.data === "TOKEN_REQUEST") {
          sendToken();
        } else if (e.data === "IFRAME_LOADED") {
          sendQueryPayload();
        } else if (e.data === "QUERY_SUCCESSFULLY_SUBMITTED") {
          handleCloseResubmitModal();
        }
        return;
      }

      // --- New structured messages ---
      const { type } = e.data || {};

      if (type === "TOKEN_REQUEST") {
        sendToken();
      } else if (type === "IFRAME_LOADED") {
        sendQueryPayload();
      } else if (type === "QUERY_SUCCESSFULLY_SUBMITTED") {
        handleCloseResubmitModal();
      } else if (type === "OPENQUERY_START_JOB") {
        // ***** PROXY CALL: parent calls /openquery/jobs, NOT the iframe *****
        const { categoryId, definition } = e.data || {};

        if (!definition) {
          console.error("OPENQUERY_START_JOB missing definition:", e.data);
          return;
        }

        (async () => {
          try {
            await Auth.currentAuthenticatedUser();
            const response = await API.post("OpenQueryAPI", "/openquery/jobs", {
              body: definition,
            });

            const status =
              (response && response.statusCode) ||
              (response && response.status) ||
              200;

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: true,
                status,
                body: response,
                categoryId,
              },
              targetOrigin
            );
          } catch (err) {
            console.error("error submitting openquery job from parent", err);

            const status =
              err?.response?.status ||
              err?.response?.statusCode ||
              500;

            const message =
              err?.response?.data?.message ||
              err?.message ||
              "Job submit failed";

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: false,
                status,
                body: { message },
                categoryId,
              },
              targetOrigin
            );
          }
        })();
      }
    },
    [modalResubmitData]
  );

  useEffect(() => {
    window.addEventListener("message", handleIframeRequests);
    return () => window.removeEventListener("message", handleIframeRequests);
  }, [handleIframeRequests]);

  const displayList = useMemo(() => {
    if (!queries) return [];
    return queries
      .filter((query) =>
        [
          query.description || "",
          query.id,
          query.report.split(".").join(", "),
          query.dateCreated.toLocaleDateString(),
          query.status,
        ]
          .join("")
          .toLowerCase()
          .includes(filterString.toLowerCase())
      )
      .sort((a, b) => {
        if (!sortColumn) return 0;
        if (sortDirection === "ascending") {
          return sortKey(a) > sortKey(b) ? 1 : sortKey(a) < sortKey(b) ? -1 : 0;
        } else {
          return sortKey(a) > sortKey(b) ? -1 : sortKey(a) < sortKey(b) ? 1 : 0;
        }
      })
      .slice(paginationOffset, paginationOffset + paginationSize);
  }, [queries, paginationOffset, paginationSize, filterString, sortColumn, sortKey, sortDirection]);

  return (
    <>
      <div className="card">
        <div
          className="card-header text-light font-weight-bold container-fluid"
        >
          <div className="row">
            <div className="col">Queries</div>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="row m-0">
            <div className="col"></div>
            <div className="col-auto mt-3">
              {queries && (
                <PhGridFilter
                  filterString={filterString}
                  setFilterString={setFilterString}
                  setPaginationOffset={setPaginationOffset}
                />
              )}
            </div>
          </div>
          <table className="table table-striped table-responsive" style={{ padding: 0 }}>
            <thead>
              <tr>
                <th scope="col">
                  <PhGridSort
                    columnLabel="File Name"
                    columnName={(d) => d?.description}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn,
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Query Id"
                    columnName={(d) => d?.id}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn,
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Report Name"
                    columnName={(d) => d?.report.split(".").join(", ")}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn,
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Created"
                    columnName={(d) => d?.dateCreated}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn,
                    }}
                  />
                </th>
                <th scope="col">
                  <PhGridSort
                    columnLabel="Status"
                    columnName={(d) => d?.status}
                    config={{
                      sortKey,
                      setSortKey,
                      sortDirection,
                      setSortDirection,
                      sortColumn,
                      setSortColumn,
                    }}
                  />
                </th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {!queries && (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner />
                  </td>
                </tr>
              )}
              {displayList.map((item) => (
                <QueryRow
                  {...item}
                  key={item.id}
                  setShowResubmitModal={setShowResubmitModal}
                  setModalCategoryId={setModalCategoryId}
                  setModalResubmitData={setModalResubmitData}
                  setShowReminderModal={setShowReminderModal}
                  job={item}
                  setReminderJob={setReminderJob}
                />
              ))}
            </tbody>
          </table>
          {queries && (
            <div className="row m-0 justify-content-end" style={{ paddingRight: ".75rem" }}>
              <PhGridPagination
                fullList={queries}
                paginationOffset={paginationOffset}
                setPaginationOffset={setPaginationOffset}
                setPaginationSize={setPaginationSize}
                paginationSize={paginationSize}
                filterString={filterString}
              />
            </div>
          )}
        </div>
      </div>

      <Modal show={errorMsg} onHide={() => setErrorMsg("")}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMsg}</p>
        </Modal.Body>
      </Modal>

      <Modal size="lg" show={showResubmitModal} onHide={handleCloseResubmitModal}>
        <Modal.Header closeButton>
          <Modal.Title>Resubmit Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 embed-responsive embed-responsive-16by9">
                {showResubmitModal && modalCategoryId && (
                  <iframe
                    // Uses resolved base (local → localhost, else env domain)
                    src={`${targetOrigin}/${modalCategoryId}/index.html`}
                    title="Perception Health Report Request"
                    className="embed-responsive-item"
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {remindersEnabled && (
        <OpenQueryReminderModal
          title="Schedule Reminder"
          job={reminderJob}
          showReminderModal={showReminderModal}
          handleCloseReminderModal={handleCloseReminderModal}
        />
      )}
    </>
  );
}

export default OpenQueryQueriesContainer;
